/**
 * Phase K.4 (2026-08-07) — Shared credit-memos display card.
 *
 * Renders on customer portal (invoice + statement detail), public
 * tokenized views, and admin detail pages. Read-only. Callers pass
 * ``memos`` (list of ``credit_memos`` docs from the API), ``paidCents``
 * (original paid amount), and ``refundedCents`` (sum of non-reversed
 * memo amounts — server-computed, single source of truth for the net
 * balance line).
 *
 * Data-testids: ``credit-memos-card``, ``credit-memos-row-{memo_id}``,
 * ``credit-memos-net-due``.
 */
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Undo2 } from "lucide-react";

const REASON_LABEL = {
    requested_by_customer: "Customer request",
    duplicate: "Duplicate charge",
    fraudulent: "Fraud",
    other: "Other",
};

const STATUS_COLOR = {
    succeeded: "bg-emerald-100 text-emerald-900 ring-emerald-200",
    pending: "bg-amber-50 text-amber-900 ring-amber-200",
    failed: "bg-red-50 text-red-700 ring-red-200",
    reversed: "bg-slate-100 text-slate-500 ring-slate-200 line-through",
};

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
}
function fmtDate(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString(); } catch { return s; }
}

export default function CreditMemosCard({
    memos, paidCents, refundedCents,
    heading = "Credit memos",
    showBalanceLine = true,
}) {
    if (!memos || memos.length === 0) return null;
    const paid = Number(paidCents ?? 0);
    const refunded = Number(refundedCents ?? 0);
    const netDue = Math.max(0, paid - refunded);

    return (
        <Card className="mt-4" data-testid="credit-memos-card">
            <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                    <Undo2 className="h-4 w-4 text-slate-500" />
                    <h2 className="text-sm font-semibold text-slate-900">
                        {heading} ({memos.length})
                    </h2>
                </div>
                <div className="space-y-2">
                    {memos.map((m) => (
                        <div key={m.id}
                             className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2 last:border-0 last:pb-0"
                             data-testid={`credit-memos-row-${m.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-xs text-slate-700">
                                        {m.memo_number || "—"}
                                    </span>
                                    <span
                                        className={`text-[10px] px-1.5 py-0.5 rounded ring-1 ${
                                            STATUS_COLOR[m.status] || "bg-slate-100 ring-slate-200"
                                        }`}
                                    >
                                        {m.status || "pending"}
                                    </span>
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    {REASON_LABEL[m.reason] || m.reason}
                                    <span className="mx-1">·</span>
                                    {fmtDate(m.created_at)}
                                </div>
                                {m.notes && (
                                    <div className="text-xs text-slate-600 mt-0.5 italic">
                                        {m.notes}
                                    </div>
                                )}
                            </div>
                            <div className="text-right">
                                <div className={`text-sm font-semibold tabular-nums ${
                                    m.status === "reversed"
                                        ? "text-slate-400 line-through"
                                        : "text-emerald-800"
                                }`}>
                                    -{fmtMoney(m.amount_cents)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {showBalanceLine && (
                    <div className="mt-4 pt-3 border-t border-slate-200 space-y-1 text-sm">
                        <div className="flex items-center justify-between text-slate-600">
                            <span>Original paid</span>
                            <span className="tabular-nums">{fmtMoney(paid)}</span>
                        </div>
                        <div className="flex items-center justify-between text-emerald-800">
                            <span>Credit memos</span>
                            <span className="tabular-nums">-{fmtMoney(refunded)}</span>
                        </div>
                        <div className="flex items-center justify-between font-semibold text-slate-900 pt-1 border-t"
                             data-testid="credit-memos-net-due">
                            <span>Net</span>
                            <span className="tabular-nums">{fmtMoney(netDue)}</span>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
